import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Globe2, Users, TrendingUp } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import missionIllustration from "@/assets/about-mission-illustration.webp";
import { CtaTopScribble, CtaUnderlineScribble } from "@/components/site/VectorMarks";
import { breadcrumbSchema, breadcrumbsFromPath } from "@/lib/structuredData";


const values = [
  { icon: ShieldCheck, title: "Reliability First", desc: "We build infrastructure that enterprises can depend on for their most critical communications.", color: "text-brand" },
  { icon: Globe2, title: "Global Perspective", desc: "We think globally, with direct presence and expertise across major markets worldwide.", color: "text-sky" },
  { icon: Users, title: "Customer Focus", desc: "We succeed when our customers succeed. Our team is dedicated to your messaging goals.", color: "text-amber" },
  { icon: TrendingUp, title: "Continuous Improvement", desc: "We constantly invest in our network, technology, and team to deliver better service.", color: "text-violet" },
];

const numbers = [
  { value: "200+", label: "Countries Covered" },
  { value: "1B+", label: "Messages Monthly" },
  { value: "99.99%", label: "Platform Uptime" },
  { value: "24/7", label: "Global Support" },
];

const faqs = [
  { question: "What is Yootelco and what services does it provide?", answer: "Yootelco is an enterprise telecom messaging platform delivering A2P SMS, OTP authentication, voice termination, and transactional messaging solutions for businesses worldwide." },
  { question: "How long has Yootelco been operating in the telecom industry?", answer: "Yootelco brings 18+ years of telecom expertise, combining deep carrier relationships, technical infrastructure, and industry knowledge to deliver reliable messaging solutions globally." },
  { question: "Which countries does Yootelco operate in?", answer: "Yootelco provides coverage across 200+ countries through direct Tier-1 carrier connections and vetted aggregator partnerships, serving enterprises across all major global markets." },
  { question: "What makes Yootelco different from other messaging providers?", answer: "Yootelco combines carrier-grade infrastructure, white-route only policy, 99.99% uptime SLA, and dedicated industry expertise, built specifically for enterprises that cannot afford delivery failures." },
  { question: "What industries does Yootelco serve?", answer: "Yootelco serves fintech, banking, retail, iGaming, telecom, digital marketing, real estate, education, and travel industries with specialized messaging solutions for each sector." },
  { question: "How can I contact Yootelco or request a demo?", answer: "You can reach Yootelco at info@yootelco.com or call +44 207 0999469. Our team is available 24/7 to discuss your messaging needs and provide tailored solutions." },
];

const About = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const url = "https://yootelco.com/about";
  const metaTitle = "About Yootelco | Global Telecom Solutions Since 2008";
  const metaDescription =
    "Global telecom company delivering A2P SMS, OTP verification, wholesale voice and SIP trunking to MNOs, fintechs and enterprises worldwide.";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
    foundingDate: "2008",
    numberOfEmployees: "100",
    description:
      "Global telecom solutions provider offering A2P SMS, OTP verification, wholesale voice and SIP trunking since 2008.",
    address: [
      { "@type": "PostalAddress", addressLocality: "Waltham Cross", addressCountry: "GB" },
      { "@type": "PostalAddress", addressLocality: "Hong Kong", addressCountry: "HK" },
      { "@type": "PostalAddress", addressLocality: "Yerevan", addressCountry: "AM" },
    ],
  };

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(breadcrumbsFromPath("/about")))}
        </script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[900px] text-center">
            <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
              About Yootelco
            </h1>
            <p className="mx-auto mt-[18px] max-w-[680px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px]">
              Enterprise telecom messaging infrastructure built for reliability. We connect businesses to their customers worldwide through SMS, voice, and high-reliability messaging routes.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto grid max-w-[1120px] grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14">
          <div className="flex justify-center">
            <div className="relative w-full max-w-[560px]">
              {/* Soft color glow backdrop */}
              <div className="absolute -left-8 -top-8 h-44 w-44 rounded-full bg-emerald-400/40 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-6 h-48 w-48 rounded-full bg-emerald-300/40 blur-3xl" aria-hidden="true" />

              <div className="relative overflow-hidden rounded-[28px] shadow-lift ring-1 ring-black/[0.04]">
                <img
                  src={missionIllustration}
                  alt="Yootelco messaging illustration"
                  className="block h-auto w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <div>
            <h2 className="section-title text-left">Our Mission</h2>
            <p className="mt-5 text-[15px] leading-[1.7] text-muted-foreground md:text-[16px]">
              Yootelco exists to provide enterprises with messaging infrastructure they can trust. In a world where communication is critical to business operations, we deliver the reliability, coverage, and support that enterprises demand.
            </p>
            <p className="mt-5 text-[15px] leading-[1.7] text-muted-foreground md:text-[16px]">
              We've built our platform on direct carrier relationships, robust technology, and deep industry expertise. Whether you're sending OTP codes, transaction alerts, or scaling a global communications platform, Yootelco provides the foundation for reliable messaging.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Our Values</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1080px] grid-cols-1 gap-5 md:grid-cols-2">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <article key={v.title} className="flex flex-col rounded-[18px] border border-border/60 bg-card px-7 py-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                <FlatIcon Icon={Icon} color={v.color} />
                <h3 className="mt-[18px] text-[18px] font-bold text-foreground">{v.title}</h3>
                <p className="mt-[10px] text-[14px] leading-[1.65] text-muted-foreground">{v.desc}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Our Numbers */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[1200px] rounded-[24px] bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 px-6 py-12 md:px-12 md:py-14">
          <h2 className="text-center font-display text-[28px] font-bold text-white md:text-[34px]">By the Numbers</h2>
          <div className="mt-8 grid grid-cols-2 gap-y-10 md:grid-cols-4 md:gap-0">
            {numbers.map((s, i) => (
              <div key={s.label} className={`text-center ${i > 0 ? "md:border-l md:border-white/20" : ""}`}>
                <div className="font-display text-[36px] font-bold leading-none text-white md:text-[44px]">{s.value}</div>
                <div className="mt-3 text-[13px] font-medium uppercase tracking-wider text-white/80 md:text-[13.5px]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="section-shell">
        <div className="cta-shell relative isolate overflow-hidden rounded-[36px] border border-border/70 px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <div className="cta-grid-overlay absolute inset-0" aria-hidden="true" />
          <CtaTopScribble className="absolute right-5 top-4 z-0 h-auto w-[108px] opacity-80 md:right-8 md:top-6 md:w-[126px]" />
          <div className="relative z-10">
            <h2 className="section-title">
              Ready to get started?
            </h2>
            <div className="mx-auto mt-5 flex max-w-2xl flex-col items-center">
              <p className="text-lg text-muted-foreground">Talk to our team about call center solutions tailored to your business.</p>
              <CtaUnderlineScribble className="mt-2 h-auto w-[92px]" />
            </div>
            <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default About;